import React from 'react';
import { CheckCircle, Clock, Eye, XCircle, AlertTriangle } from 'lucide-react';
import { ContentStatus } from '../../types/database';

interface StatusBadgeProps {
  status: ContentStatus | 'all';
  size?: 'sm' | 'md';
}

export function StatusBadge({ status, size = 'sm' }: StatusBadgeProps) {
  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
  };

  const iconSize = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4';

  switch (status) {
    case 'draft':
      return (
        <span className={`inline-flex items-center gap-1 font-medium bg-gray-100 text-gray-700 rounded-md ${sizeClasses[size]}`}>
          <Clock className={iconSize} />
          Draft
        </span>
      );
    case 'pending':
      return (
        <span className={`inline-flex items-center gap-1 font-medium bg-yellow-100 text-yellow-700 rounded-md ${sizeClasses[size]}`}>
          <Eye className={iconSize} />
          Pending Review
        </span>
      );
    case 'published':
      return (
        <span className={`inline-flex items-center gap-1 font-medium bg-green-100 text-green-700 rounded-md ${sizeClasses[size]}`}>
          <CheckCircle className={iconSize} />
          Published
        </span>
      );
    case 'all':
      return (
        <span className={`inline-flex items-center gap-1 font-medium bg-blue-100 text-blue-700 rounded-md ${sizeClasses[size]}`}>
          All
        </span>
      );
    default:
      return null;
  }
}

interface RiskBadgeProps {
  riskTag?: string | null;
  size?: 'sm' | 'md';
}

export function RiskBadge({ riskTag, size = 'sm' }: RiskBadgeProps) {
  if (!riskTag) return null;

  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
  };

  const iconSize = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4';

  const getBadgeStyle = () => {
    switch (riskTag.toLowerCase()) {
      case 'suicide':
      case 'selvmord':
        return 'bg-red-100 text-red-700';
      case 'violence':
      case 'vold':
        return 'bg-orange-100 text-orange-700';
      case 'self-harm':
      case 'selvskading':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <span className={`inline-flex items-center gap-1 font-medium rounded-md ${getBadgeStyle()} ${sizeClasses[size]}`}>
      <AlertTriangle className={iconSize} />
      {riskTag}
    </span>
  );
}